import { useQuery } from 'react-query'
import type { UseQueryResult } from 'react-query'
import axios from 'axios'

import { ContentfulEntry } from './__generated__/contentful'

export * from './__generated__/contentful/TypeMarkdownDocument'

const { REACT_APP_CONTENTFUL_SPACE_ID, REACT_APP_CONTENTFUL_TOKEN } = process.env

export const fetchContentfulEntry = (entryId: string) => async () => {
  const response = await axios.get(
    `https://cdn.contentful.com/spaces/${REACT_APP_CONTENTFUL_SPACE_ID}/environments/master/entries/${entryId}?access_token=${REACT_APP_CONTENTFUL_TOKEN}`,
  )
  return response.data
}

export type UseQueryResultResponse<E = ContentfulEntry> = UseQueryResult<E> & {
  entry?: E
}

/**
 * Fetch contentful entry by id using Contentful Content Delivery API:
 * https://www.contentful.com/developers/docs/references/content-delivery-api/.
 *
 * This is a wrapper around `useQuery`, that returns everything `useQuery` returns and adds `entry` field.
 * So you can use `isLoading`, `isError` and other fields returned by `useQuery`.
 *
 * ```typescript
 * const { entry, isLoading } = useContentfulEntry(entryId)
 * ```
 *
 * The type of `entry` is a union of all possible entry types for your Contentful space. They are generated
 * by `yarn types:contentful`. You can narrow it down with auto-generated type guards. For example:
 *
 * ```typescript
 * import {
 *   useContentfulEntry,
 *   isTypeMarkdownDocument
 * } from "hooks/useContentfulEntry"
 *
 * ...
 *
 * const { entry } = useContentfulEntry(entryId)
 * if (entry && isTypeMarkdownDocument(entry)) {
 *   // entry is definitely a TypeMarkdownDocument here
 * }
 * ```
 */
export const useContentfulEntry = <E = ContentfulEntry>(
  entryId: string,
): UseQueryResultResponse<E> => {
  const oneDay = 1000 * 60 * 60 * 24

  const { data, ...rest } = useQuery(['contentful-entry', entryId], fetchContentfulEntry(entryId), {
    staleTime: oneDay,
    cacheTime: oneDay,
  })

  return {
    entry: data,
    data,
    ...rest,
  }
}
